// scanShared.jsx — shared primitives and helpers for the Existing
// Attachment Field Scan tool (design_handoff_attachment_field_scan).
//
// Confidence tags, the accept checkbox, the compact filter-chip dropdown
// (queue rail + history filter bar) and the attachment thumbnail. Styled
// from T.* tokens, inline styles per codebase convention.

import { Fragment, useState } from 'react';
import { T } from '../../theme';

export const CONF_COLOR = {
  High: { badge: T.badge.green, dot: T.green500 },
  Medium: { badge: T.badge.orange, dot: T.orange500 },
  Low: { badge: T.badge.red, dot: T.red500 },
};

// Cache entries carry confidence as 'high'|'medium'|'low' strings (current
// prompt) or 0–1 numbers (the retired bbox prompt, still present in old
// cache rows). Normalize to the design's High/Medium/Low.
export function normalizeConf(confidence) {
  if (confidence == null) return null;
  if (typeof confidence === 'number') {
    return confidence >= 0.8 ? 'High' : confidence >= 0.5 ? 'Medium' : 'Low';
  }
  const c = String(confidence).toLowerCase();
  if (c === 'high') return 'High';
  if (c === 'medium' || c === 'med') return 'Medium';
  if (c === 'low') return 'Low';
  return null;
}

export function ConfTag({ conf, style = {} }) {
  const c = CONF_COLOR[conf];
  if (!c) return null;
  return (
    <span style={{
      background: c.badge, color: T.ink, borderRadius: T.radiusPill, padding: '0 8px',
      height: 20, display: 'inline-flex', alignItems: 'center',
      font: `700 12px/20px ${T.fontSans}`, whiteSpace: 'nowrap', ...style,
    }}>
      {conf}
    </span>
  );
}

export function AcceptCheck({ on, onClick, size = 18 }) {
  return (
    <span onClick={onClick} style={{
      width: size, height: size, borderRadius: 3, flexShrink: 0, display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center', cursor: onClick ? 'pointer' : 'default',
      background: on ? T.brand : T.white,
      border: `1.5px solid ${on ? T.brand : T.borderStrong}`,
      color: T.white, fontSize: size * 0.6, boxSizing: 'border-box',
    }}>
      {on && <i className="fa-solid fa-check" />}
    </span>
  );
}

// Compact dropdown filter chip: pill button + popover listing options with
// per-option counts and an "All …" row. `options`: [{ name, count }].
export function ScanFilterChip({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', height: 26, padding: '0 9px', borderRadius: T.radiusPill, cursor: 'pointer',
        background: value ? T.blue50 : T.white, border: `1px solid ${value ? T.blue200 : T.border}`,
        font: `500 12px/18px ${T.fontSans}`, color: T.ink,
        display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0,
      }}>
        <span style={{ color: T.inkMuted, flexShrink: 0 }}>{label}:</span>
        <b style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || 'All'}</b>
        <i className="fa-solid fa-caret-down" style={{ fontSize: 10, color: T.inkMuted, marginLeft: 'auto', flexShrink: 0 }} />
      </button>
      {open && (
        <Fragment>
          <div style={{ position: 'fixed', inset: 0, zIndex: 30 }} onClick={() => setOpen(false)} />
          <div style={{
            position: 'absolute', top: 30, left: 0, zIndex: 31, minWidth: 200, background: T.white,
            border: `1px solid ${T.border}`, borderRadius: T.radiusTable, boxShadow: T.shadowMd, padding: 4,
          }}>
            {[{ name: null, count: options.reduce((n, o) => n + o.count, 0) }, ...options].map((o) => {
              const active = o.name === value;
              return (
                <div key={o.name || '__all'} onClick={() => { onChange(o.name); setOpen(false); }} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 9px', borderRadius: 4, cursor: 'pointer',
                  background: active ? T.blue50 : 'transparent',
                  font: `${active ? 600 : 400} 12.5px/17px ${T.fontSans}`, color: T.ink,
                }}>
                  <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {o.name || `All ${label.toLowerCase()}s`}
                  </span>
                  <span style={{ font: `400 11.5px/16px ${T.fontSans}`, color: T.inkSoft }}>{o.count}</span>
                  {active && <i className="fa-solid fa-check" style={{ fontSize: 10, color: T.brand }} />}
                </div>
              );
            })}
          </div>
        </Fragment>
      )}
    </div>
  );
}

// 34×19 toggle, brand when on, 140ms knob slide.
export function ScanToggle({ on, onClick }) {
  return (
    <span onClick={onClick} style={{
      width: 34, height: 19, borderRadius: T.radiusPill, flexShrink: 0, cursor: 'pointer', position: 'relative',
      background: on ? T.brand : T.borderStrong, transition: 'background 140ms ease-out', display: 'inline-block',
    }}>
      <span style={{
        position: 'absolute', top: 2, left: on ? 17 : 2, width: 15, height: 15, borderRadius: T.radiusPill,
        background: T.white, boxShadow: '0 1px 2px rgba(0,0,0,0.25)', transition: 'left 140ms ease-out',
      }} />
    </span>
  );
}

// Attachment thumbnail (104×60 in the desk). Shows the real image when
// bytes are loaded, otherwise a file-type icon.
export function ScanThumb({ att, w = 104, h = 60, active, onClick }) {
  const isImg = att.classification === 'image' && att.base64;
  return (
    <div style={{ width: w, cursor: 'pointer' }} onClick={onClick}>
      <div style={{
        height: h, position: 'relative', background: T.bgMuted, overflow: 'hidden',
        border: `1px solid ${active ? T.brand : T.borderSubtle}`,
        boxShadow: active ? `inset 0 0 0 1px ${T.brand}` : 'none',
        borderRadius: T.radiusTable, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.inkSoft,
      }}>
        {isImg
          ? <img src={`data:${att.contentType};base64,${att.base64}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <i className={`fa-solid fa-${att.classification === 'document' ? 'file-pdf' : 'image'}`} style={{ fontSize: 22 }} />}
      </div>
      <div style={{ font: `400 11px/14px ${T.fontSans}`, color: T.inkMuted, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {att.filename || `#${att.id}`}
      </div>
    </div>
  );
}

export function formatRelative(ts) {
  if (!ts) return '';
  const ms = Date.now() - new Date(ts).getTime();
  if (!Number.isFinite(ms) || ms < 0) return 'just now';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mon = Math.floor(day / 30);
  if (mon < 12) return `${mon}mo ago`;
  return `${Math.floor(mon / 12)}y ago`;
}

// "site.gofmx.com" (possibly with protocol) → https equipment URL.
export function fmxEquipmentUrl(siteUrl, equipmentId) {
  if (!siteUrl) return '#';
  const host = String(siteUrl).replace(/^https?:\/\//, '').replace(/\/+$/, '');
  return `https://${host}/equipment/${equipmentId}`;
}
